import React, { useState, useMemo } from 'react';
import { Activity } from 'lucide-react';
import CalculatorLayout from '../../components/calculators/CalculatorLayout';
import { HeightInput, WeightInput } from '../../components/calculators/MeasureInputs';
import { NumberField, SelectField, GenderField } from '../../components/calculators/FormFields';
import { EmptyResult, PrimaryResult, StatRow } from '../../components/calculators/ResultCard';
import { calculateBMR, calculateTDEE, getCalorieTargets, ACTIVITY_FACTORS } from '../../lib/calculatorUtils';

const activityOptions = Object.entries(ACTIVITY_FACTORS).map(([k, v]) => ({
  value: k, label: `${v.label}: ${v.desc}`
}));

const TDEECalculatorPage = () => {
  const [unit, setUnit] = useState('metric');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('female');
  const [heightCm, setHeightCm] = useState('');
  const [weightKg, setWeightKg] = useState('');
  const [activity, setActivity] = useState('moderate');

  const result = useMemo(() => {
    const a = parseInt(age, 10);
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    if (!a || !h || !w || a < 10 || h < 50 || w < 20) return null;
    const bmr = calculateBMR({ gender, weightKg: w, heightCm: h, age: a });
    const tdee = calculateTDEE(bmr, activity);
    const targets = getCalorieTargets(tdee);
    return {
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      ...targets
    };
  }, [age, gender, heightCm, weightKg, activity]);

  const inputs = (
    <div className="space-y-5" data-testid="tdee-inputs">
      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Your Profile</h2>
      <p className="text-slate-400 text-sm mb-5">Mifflin-St Jeor BMR × activity factor.</p>
      <div className="grid grid-cols-2 gap-3">
        <NumberField id="tdee-age" label="Age" value={age} onChange={setAge} placeholder="e.g. 28" suffix="yrs" min={10} max={120} testId="tdee-age-input" required />
        <GenderField value={gender} onChange={setGender} testIdPrefix="tdee-gender" />
      </div>
      <HeightInput unit={unit} onUnitChange={setUnit} heightCm={heightCm} onHeightCmChange={setHeightCm} />
      <WeightInput unit={unit} onUnitChange={setUnit} weightKg={weightKg} onWeightKgChange={setWeightKg} />
      <SelectField id="tdee-activity" label="Activity Level" value={activity} onChange={setActivity} options={activityOptions} testId="tdee-activity-select" required />
    </div>
  );

  const resultNode = result ? (
    <div className="flex flex-col h-full">
      <PrimaryResult label="Your TDEE" value={result.tdee.toLocaleString()} unit="kcal/day" accent="from-emerald-500 to-green-500" testId="tdee-result-value" />
      <div className="mt-8 space-y-1">
        <StatRow label="BMR (resting)" value={result.bmr.toLocaleString()} unit="kcal" testId="tdee-bmr" />
        <StatRow label="Maintenance" value={result.maintenance.toLocaleString()} unit="kcal" color="text-emerald-300" testId="tdee-maintenance" />
        <StatRow label="Mild fat loss (−300)" value={result.mild.toLocaleString()} unit="kcal" color="text-orange-300" testId="tdee-fat-loss" />
        <StatRow label="Moderate fat loss (−500)" value={result.moderate.toLocaleString()} unit="kcal" color="text-orange-400" />
        <StatRow label="Aggressive fat loss (−700)" value={result.aggressive.toLocaleString()} unit="kcal" color="text-rose-400" />
        <StatRow label="Muscle gain (+300)" value={result.muscleGain.toLocaleString()} unit="kcal" color="text-blue-300" testId="tdee-muscle-gain" />
      </div>
      <p className="text-slate-500 text-xs mt-6 leading-relaxed">
        TDEE is an estimate. Track for 2–3 weeks and adjust by 100–200 kcal if weight is not trending as expected.
      </p>
    </div>
  ) : (
    <EmptyResult message="Enter your age, gender, height, weight and activity level to see your TDEE." />
  );

  return (
    <CalculatorLayout
      slug="tdee"
      title="TDEE Calculator"
      subtitle="Calculate your Total Daily Energy Expenditure, the calories you burn each day at your activity level."
      metaDescription="TDEE Calculator from TCUBE FITNESS to calculate maintenance, fat loss and muscle gain calories using the Mifflin-St Jeor equation."
      icon={Activity}
      accentGradient="from-emerald-500 to-green-500"
      accentColor="text-emerald-400"
      badgeBg="bg-emerald-500/10"
      badgeBorder="border-emerald-500/30"
      inputs={inputs}
      result={resultNode}
      formulaText={
        <>
          <p><strong className="text-slate-300">Step 1: BMR (Mifflin-St Jeor):</strong></p>
          <p>Male: BMR = (10 × kg) + (6.25 × cm) − (5 × age) + 5</p>
          <p>Female: BMR = (10 × kg) + (6.25 × cm) − (5 × age) − 161</p>
          <p><strong className="text-slate-300">Step 2: TDEE:</strong> TDEE = BMR × activity factor</p>
          <p>Sedentary 1.2 · Light 1.375 · Moderate 1.55 · Very Active 1.725 · Athlete 1.9</p>
        </>
      }
    />
  );
};

export default TDEECalculatorPage;
